
/* eslint-disable  */
import { useState, useEffect, useRef } from 'react';

interface PresenceTimerData {
  timerStart: number;
  timerEnd: number;
  duration: number;
  autoSwitchback: boolean;
  userDisplay: string;
}

export type TimerState =
  | { kind: "NoTimer" }
  | { kind: "TimerOn"; timeDelta: number }
  | { kind: "OverStaying"; timeDelta: number }
  | { kind: "SwitchingBack" }
  | { kind: "LoadingTimer" };

interface UsePresenceTimerProps {
  messageTimestamp?: number;
  presenceTimerData?: PresenceTimerData;
  late?: boolean;
  elapsedTime: string;
  offset: number;
  display?: string;
}

interface UsePresenceTimerReturn {
  // Regular presence duration (time since status change) - only if messageTimestamp provided
  duration: number | undefined;
  
  // Overstay time for late users (time since timer expired)
  overstayTime: number | undefined;
  
  // Timer state for ExPresence component
  timerState: TimerState;
  
  // Formatted duration strings
  formattedDuration: string | undefined;
  formattedOverstayTime: string | undefined;
}

// format duration for elapsedTime="full"
const formatDuration = (time: number) => {
  if (time < 0) {
    return "00:00";
  }
  const durationSeconds = time;
  const durationMinutes = Math.floor(durationSeconds / 60);
  const durationHours =
    durationMinutes >= 60 ? Math.floor(durationMinutes / 60) : "";
  const seconds = durationSeconds % 60;
  const minutes = durationMinutes % 60;
  return durationHours !== ""
    ? `${durationHours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
};

// format duration for elapsedTime="approximate"
const formatApproxDuration = (sec: number) => {
  if (sec < 0) {
    return "0s";
  }
  if (sec < 60) {
    return `${sec}s`;
  }
  if (sec < 3600) {
    return `${Math.floor(sec / 60)}m`;
  }
  if (sec < 86400) {
    return `${Math.floor(sec / 3600)}h`;
  }
  return `${Math.floor(sec / 86400)}d`;
};

export const usePresenceTimer = ({
  messageTimestamp,
  presenceTimerData,
  late = false,
  elapsedTime,
  offset,
  display
}: UsePresenceTimerProps): UsePresenceTimerReturn => {
  const [duration, setDuration] = useState<number | undefined>();
  const [overstayTime, setOverstayTime] = useState<number | undefined>();
  const [timerState, setTimerState] = useState<TimerState>({ kind: "NoTimer" });
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle regular presence duration timer
  useEffect(() => {
    if (messageTimestamp === undefined || elapsedTime === "none") {
      setDuration(undefined);
      return () => {};
    }

    // Set initial duration
    if (duration === undefined) {
      setDuration(new Date().getTime() + offset - messageTimestamp);
      return () => {};
    }

    let cb: NodeJS.Timeout;
    if (elapsedTime === "full") {
      cb = setTimeout(() => {
        setDuration(new Date().getTime() + offset - messageTimestamp);
      }, 3000);
    } else if (elapsedTime === "approximate") {
      if (duration < 60000) {
        cb = setTimeout(() => {
          setDuration(new Date().getTime() + offset - messageTimestamp);
        }, new Date().getTime() - messageTimestamp + offset - duration + 1000);
      } else if (duration < 3600000) {
        cb = setTimeout(() => {
          setDuration(new Date().getTime() + offset - messageTimestamp);
        }, new Date().getTime() - messageTimestamp + offset - duration + 60000);
      } else if (duration < 86400000) {
        cb = setTimeout(() => {
          setDuration(new Date().getTime() + offset - messageTimestamp);
        }, new Date().getTime() - messageTimestamp + offset - duration + 3600000);
      } else {
        cb = setTimeout(() => {
          setDuration(new Date().getTime() + offset - messageTimestamp);
        }, new Date().getTime() - messageTimestamp + offset - duration + 86400000);
      }
    }

    return () => {
      if (cb) clearTimeout(cb);
    };
  }, [offset, messageTimestamp, duration, elapsedTime]);

  // Handle overstay timer for late users
  useEffect(() => {
    console.log('usePresenceTimer: Overstay effect triggered', { 
      late, 
      hasPresenceTimerData: !!presenceTimerData, 
      elapsedTime,
      presenceTimerData 
    });
    
    if (!late || !presenceTimerData || elapsedTime === "none") {
      setOverstayTime(undefined);
      return () => {};
    }

    const { timerEnd } = presenceTimerData;
    
    // Calculate initial overstay time
    const calculateOverstay = () => Math.max(0, new Date().getTime() + offset - timerEnd);
    
    // Set initial overstay time
    const initialOverstay = calculateOverstay();
    console.log('usePresenceTimer: Setting initial overstay time', { 
      initialOverstay, 
      timerEnd, 
      currentTime: new Date().getTime() + offset 
    });
    setOverstayTime(initialOverstay);

    let intervalId: NodeJS.Timeout;
    if (elapsedTime === "full") {
      // Update every second for full format
      intervalId = setInterval(() => {
        setOverstayTime(calculateOverstay());
      }, 1000);
    } else if (elapsedTime === "approximate") {
      // For approximate, update every second initially, then adjust based on duration
      intervalId = setInterval(() => {
        const currentOverstay = calculateOverstay();
        setOverstayTime(currentOverstay);
        
        // Adjust interval based on current overstay time
        if (currentOverstay >= 60000 && currentOverstay < 3600000) {
          // Clear current interval and set new one for minute updates
          clearInterval(intervalId);
          intervalId = setInterval(() => {
            setOverstayTime(calculateOverstay());
          }, 60000);
        } else if (currentOverstay >= 3600000 && currentOverstay < 86400000) {
          // Clear current interval and set new one for hour updates
          clearInterval(intervalId);
          intervalId = setInterval(() => {
            setOverstayTime(calculateOverstay());
          }, 3600000);
        } else if (currentOverstay >= 86400000) {
          // Clear current interval and set new one for day updates
          clearInterval(intervalId);
          intervalId = setInterval(() => {
            setOverstayTime(calculateOverstay());
          }, 86400000);
        }
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [offset, presenceTimerData, elapsedTime, late]);

  // Handle timer state for ExPresence component
  useEffect(() => {
    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // First, check if timer data matches current display
    if (presenceTimerData && presenceTimerData.userDisplay !== display) {
      setTimerState({ kind: "NoTimer" });
      return;
    }

    // Check if current status is a timed status that should show a timer
    const isTimedStatus = display && (
      display.startsWith("DND") || 
      display.startsWith("BSY") || 
      display.startsWith("INV") ||
      display.startsWith("AVL")
    );

    // If no timer data or not in a timed status, clear timer
    if (!presenceTimerData || !isTimedStatus) {
      setTimerState({ kind: "NoTimer" });
      return;
    }

    const { timerEnd, autoSwitchback } = presenceTimerData;
    const now = Date.now();
    const delta = timerEnd - now;

    if (delta > 0) {
      // Timer is still active - count down
      setTimerState({ kind: "TimerOn", timeDelta: delta });
      
      // Set up interval to update timer
      timerIntervalRef.current = setInterval(() => {
        const currentDelta = timerEnd - Date.now();
        if (currentDelta > 0) {
          setTimerState({ kind: "TimerOn", timeDelta: currentDelta });
        } else {
          // Timer expired
          if (autoSwitchback) {
            setTimerState({ kind: "NoTimer" });
            // Clear the interval since we're done
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
          } else {
            // For non-autoSwitchback, start counting up (overstaying)
            setTimerState({ kind: "OverStaying", timeDelta: Math.abs(currentDelta) });
            
            // Update interval to count up
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
            }
            timerIntervalRef.current = setInterval(() => {
              const overstayDelta = Math.abs(timerEnd - Date.now());
              setTimerState({ kind: "OverStaying", timeDelta: overstayDelta });
            }, 1000);
          }
        }
      }, 1000);
    } else {
      // Timer has already expired
      if (autoSwitchback) {
        setTimerState({ kind: "NoTimer" });
      } else {
        // For non-autoSwitchback, show overstaying
        setTimerState({ kind: "OverStaying", timeDelta: Math.abs(delta) });
        
        // Set up interval to count up
        timerIntervalRef.current = setInterval(() => {
          const overstayDelta = Math.abs(timerEnd - Date.now());
          setTimerState({ kind: "OverStaying", timeDelta: overstayDelta });
        }, 1000);
      }
    }

    // Cleanup function
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [presenceTimerData, display, late]);

  // Format duration strings
  const formattedDuration = duration !== undefined 
    ? elapsedTime === "full" 
      ? formatDuration(Math.round(duration / 1000))
      : formatApproxDuration(Math.round(duration / 1000))
    : undefined;

  const formattedOverstayTime = overstayTime !== undefined
    ? elapsedTime === "full"
      ? formatDuration(Math.round(overstayTime / 1000))
      : formatApproxDuration(Math.round(overstayTime / 1000))
    : undefined;

  return {
    duration,
    overstayTime,
    timerState,
    formattedDuration,
    formattedOverstayTime
  };
}; 